/**
 * Identity challenge — gates the bridge wizard.
 *
 * The recipient enters the factors the bridge asked for (`identityFactors`,
 * usually just date of birth); we hash the joined input and POST the DIGEST to
 * `/api/bridge/magic/verify-identity`. The bridge compares it against the
 * expected value IT holds and, on a match, returns an upgraded session token.
 * Every endpoint that serves PHI or accepts a submission requires that
 * upgraded token, so passing here is what actually unlocks the form.
 *
 * No plaintext value crosses any wire — only the digest, as before.
 *
 * CHANGED 2026-09-05 (case imp_bad9596e). This component used to be handed the
 * expected hash by redeem and compare it locally, so "passing" was a React
 * state transition no endpoint ever saw: possession of the link was the real
 * credential, and because the hash is an unsalted sha256 of a date of birth
 * (~36,500 candidates), shipping it also gave away the date it was protecting.
 * The attempt counter was local state a page reload reset to zero. Both now
 * live on the bridge.
 */

import React, { useState, useEffect } from "react";

import { clearBridgeSession, sha256Hex, verifyIdentity } from "../../lib/bridgeClient";

const DOB_MONTHS: Array<[string, string]> = [
  ["01", "January"], ["02", "February"], ["03", "March"], ["04", "April"],
  ["05", "May"], ["06", "June"], ["07", "July"], ["08", "August"],
  ["09", "September"], ["10", "October"], ["11", "November"], ["12", "December"],
];

interface DobParts {
  y: string;
  mo: string;
  d: string;
}

interface DobMonthDayYearProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  autoFocus?: boolean;
}

// Month / Day / Year inputs that emit a "YYYY-MM-DD" string (or "" until
// complete) — clearer for patients than a free-form date box.
function DobMonthDayYear({ value, onChange, disabled, autoFocus }: DobMonthDayYearProps) {
  const parse = (v: string): DobParts => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v || "");
    return m ? { y: m[1], mo: m[2], d: m[3] } : { y: "", mo: "", d: "" };
  };
  const [parts, setParts] = useState<DobParts>(() => parse(value));
  useEffect(() => {
    const ext = parse(value);
    if (ext.y) {
      if (ext.y !== parts.y || ext.mo !== parts.mo || ext.d !== parts.d) setParts(ext);
    } else if (!value && parts.y && parts.mo && parts.d) {
      setParts({ y: "", mo: "", d: "" });
    }
  }, [value]);
  const emit = (next: DobParts) => {
    setParts(next);
    onChange(next.y.length === 4 && next.mo && next.d ? `${next.y}-${next.mo}-${next.d}` : "");
  };
  const fs: React.CSSProperties = {
    padding: "12px 10px", fontSize: 16, border: "1px solid #e6dccb", borderRadius: 8,
    backgroundColor: disabled ? "#f3f3f3" : "white", color: "#182a33", boxSizing: "border-box", width: "100%",
  };
  const days = Array.from({ length: 31 }, (_, i) => String(i + 1).padStart(2, "0"));
  return (
    <div style={{ display: "flex", gap: 8 }}>
      <select aria-label="Month" autoFocus={autoFocus} disabled={disabled} value={parts.mo} onChange={(e) => emit({ ...parts, mo: e.target.value })} style={{ ...fs, flex: "1 1 130px" }}>
        <option value="">Month</option>
        {DOB_MONTHS.map(([v, n]) => <option key={v} value={v}>{n}</option>)}
      </select>
      <select aria-label="Day" disabled={disabled} value={parts.d} onChange={(e) => emit({ ...parts, d: e.target.value })} style={{ ...fs, flex: "0 1 90px" }}>
        <option value="">Day</option>
        {days.map((v) => <option key={v} value={v}>{String(Number(v))}</option>)}
      </select>
      <input aria-label="Year" type="number" inputMode="numeric" placeholder="Year" disabled={disabled} value={parts.y} onChange={(e) => emit({ ...parts, y: e.target.value.replace(/\D/g, "").slice(0, 4) })} style={{ ...fs, flex: "0 1 100px" }} />
    </div>
  );
}

// No local attempt limit any more: the bridge counts attempts on the
// assignment row and returns `attempts_remaining` / `locked_out`. A local
// constant here would only disagree with it — the old MAX_ATTEMPTS of 3 was
// browser state a page reload reset to zero (case imp_bad9596e).

interface FactorMeta {
  label: string;
  placeholder: string;
  autoComplete: string;
  inputMode: "numeric";
  normalize: (v: string) => string;
  validate: (v: string) => string | null;
}

// Display metadata per factor. Keys mirror what the Mac emits.
const FACTOR_META: Record<string, FactorMeta> = {
  dob: {
    label: "Date of birth",
    placeholder: "YYYY-MM-DD",
    autoComplete: "bday",
    inputMode: "numeric",
    normalize: (v) => v.trim(),
    validate: (v) =>
      /^\d{4}-\d{2}-\d{2}$/.test(v) ? null : "Please enter your date of birth as YYYY-MM-DD.",
  },
  hcn_last4: {
    label: "Last 4 digits of your health card",
    placeholder: "e.g. 1234",
    autoComplete: "off",
    inputMode: "numeric",
    normalize: (v) => v.replace(/\D/g, "").slice(-4),
    validate: (v) =>
      /^\d{4}$/.test(v) ? null : "Please enter exactly 4 digits from your health card.",
  },
};

interface DobChallengeProps {
  /** Which factors to prompt for, e.g. ["dob"]. From the redeem payload; the
   *  factor NAMES are not a secret, unlike the hashes that used to come with
   *  them. */
  identityFactors?: string[];
  onPass: (dob: string | null) => void;
  onFail?: () => void;
}

// NOTE (2026-09-05, case imp_bad9596e): this component no longer receives
// `expectedHash` / `identityCheckHash`. It used to be handed the answer and
// compare locally, which made the check cosmetic — see submit() below. The
// bridge holds the expected value and decides.

export default function DobChallenge({
  identityFactors,        // e.g. ["dob"], or ["dob", "hcn_last4"]
  onPass,
  onFail,
}: DobChallengeProps) {
  // Decide which mode + factors to render.
  const factors =
    identityFactors && identityFactors.length > 0
      ? identityFactors.filter((f) => FACTOR_META[f])
      : ["dob"];
  const isMultiFactor = factors.length > 1;

  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(factors.map((f) => [f, ""] as [string, string]))
  );
  const [error, setError] = useState<string | null>(null);
  const [burned, setBurned] = useState(false);
  // False only while the dev prefill round-trip is in flight, so the form
  // does not flash before the stage transition — but DOES appear if it fails.
  const [devAutoDone, setDevAutoDone] = useState(false);

  // DEV-ONLY convenience for /bridge-local: PREFILL the known dev date of birth
  // and submit it through the real verification path. Same double gate as
  // before — (1) an explicit build flag, VITE_DEV_BYPASS_IDENTITY === "true",
  // which lives in .env.development and is loaded by `npm run dev` but NEVER by
  // `vite build`, and (2) a localhost/loopback host; the Dockerfile is a third
  // gate, never copying .env.development into the image.
  //
  // CHANGED 2026-09-05: this used to call onPass(null) and skip the check
  // entirely. That cannot work now — the session would stay unverified and
  // every subsequent fetch would 401 — and more to the point, a client-side
  // skip of a server-side gate is exactly the shape of hole this change exists
  // to close. There is deliberately NO server-side dev bypass: the bridge
  // treats a dev request like any other.
  const DEV_BYPASS_IDENTITY =
    import.meta.env.VITE_DEV_BYPASS_IDENTITY === "true" &&
    typeof window !== "undefined" &&
    /^(localhost|127\.0\.0\.1|\[::1\])$/.test(window.location.hostname);

  const DEV_IDENTITY_DOB = import.meta.env.VITE_DEV_IDENTITY_DOB || "";

  useEffect(() => {
    if (
      DEV_BYPASS_IDENTITY && DEV_IDENTITY_DOB &&
      factors.length === 1 && factors[0] === "dob"
    ) {
      // eslint-disable-next-line no-console
      console.warn(
        "[DobChallenge] DEV: prefilling the dev DOB and verifying it for real (localhost only)"
      );
      setValues({ dob: DEV_IDENTITY_DOB });
      void (async () => {
        const candidate = await sha256Hex(DEV_IDENTITY_DOB.trim());
        try {
          const res = await verifyIdentity(candidate);
          if (res.verified) onPass(DEV_IDENTITY_DOB.trim());
          else setDevAutoDone(true);
        } catch {
          setDevAutoDone(true);  // fall through to the normal form
        }
      })();
    } else {
      setDevAutoDone(true);
    }
  }, []);

  async function submit(ev?: React.FormEvent<HTMLFormElement>) {
    ev?.preventDefault?.();
    if (burned) return;

    const normalized: Record<string, string> = {};
    for (const f of factors) {
      const meta = FACTOR_META[f];
      const v = meta.normalize(values[f] || "");
      const validationMsg = meta.validate(v);
      if (validationMsg) {
        setError(validationMsg);
        return;
      }
      normalized[f] = v;
    }

    const candidateInput = factors.map((f) => normalized[f]).join(":");
    // Single-factor legacy mode hashes the raw DOB with no separator.
    const candidate = await sha256Hex(
      factors.length === 1 ? normalized[factors[0]] : candidateInput
    );

    // THE SERVER DECIDES. Until 2026-09-05 this function compared `candidate`
    // to a hash the server had handed the browser, and "passing" was a local
    // state change no endpoint ever saw — so the link alone was the whole
    // credential, and shipping an unsalted hash of a date of birth (~36,500
    // candidates) gave away the date it was protecting (case imp_bad9596e).
    // The digest still never leaves as plaintext; only the comparison moved.
    let result: { verified: boolean; attempts_remaining: number; locked_out: boolean };
    try {
      result = await verifyIdentity(candidate);
    } catch (e) {
      setError(
        "We couldn't check that just now. Please check your connection and try again."
      );
      return;
    }

    if (result.verified) {
      onPass(normalized.dob);
      return;
    }

    // Attempts remaining come from the server and survive a page reload — the
    // old counter was React state a refresh reset to zero.
    const left = result.attempts_remaining;
    setError(
      !result.locked_out
        ? `That doesn't match what's on file. ${left} attempt${left === 1 ? "" : "s"} left.`
        : "Too many wrong attempts. Please contact your provider for a fresh link."
    );
    if (result.locked_out) {
      setBurned(true);
      clearBridgeSession();
      onFail?.();
    }
  }

  // On a local dev host the effect above prefills and verifies for real —
  // render nothing only while that round-trip is IN FLIGHT, so the form does
  // not flash before the stage transition. Once it settles the form appears
  // like anywhere else, because the dev path can now legitimately fail (wrong
  // VITE_DEV_IDENTITY_DOB, bridge down) and a blank page would be a dead end.
  if (DEV_BYPASS_IDENTITY && !devAutoDone) return null;

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#f5f0eb",
        padding: 24,
      }}
    >
      <form
        onSubmit={submit}
        style={{
          maxWidth: 420,
          width: "100%",
          backgroundColor: "white",
          padding: 32,
          borderRadius: 12,
          boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
        }}
      >
        <h2 style={{ color: "#182a33", marginTop: 0, marginBottom: 8 }}>
          {isMultiFactor ? "Confirm it's you" : "Confirm your date of birth"}
        </h2>
        <p style={{ color: "#5b6e7c", marginBottom: 24, lineHeight: 1.4 }}>
          {isMultiFactor
            ? "To make sure this link reached the right person, please confirm a couple of details."
            : "To make sure this link reached the right person, please enter your date of birth."}
        </p>

        {factors.map((f, idx) => {
          const meta = FACTOR_META[f];
          return (
            <div key={f} style={{ marginBottom: idx === factors.length - 1 ? 0 : 14 }}>
              <label
                style={{
                  display: "block",
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#182a33",
                  marginBottom: 6,
                }}
              >
                {meta.label}
              </label>
              {f === "dob" ? (
                <DobMonthDayYear
                  value={values[f]}
                  disabled={burned}
                  autoFocus={idx === 0}
                  onChange={(v) => {
                    setValues((prev) => ({ ...prev, [f]: v }));
                    if (error) setError(null);
                  }}
                />
              ) : (
                <input
                  type="text"
                  inputMode={meta.inputMode}
                  placeholder={meta.placeholder}
                  autoFocus={idx === 0}
                  autoComplete={meta.autoComplete}
                  value={values[f]}
                  disabled={burned}
                  maxLength={f === "hcn_last4" ? 4 : undefined}
                  onChange={(e) => {
                    // HCN last-4: digits only, capped at 4.
                    const v = f === "hcn_last4"
                      ? e.target.value.replace(/\D/g, "").slice(0, 4)
                      : e.target.value;
                    setValues((prev) => ({ ...prev, [f]: v }));
                    if (error) setError(null);
                  }}
                  style={{
                    width: "100%",
                    padding: "12px 14px",
                    fontSize: 16,
                    border: "1px solid #e6dccb",
                    borderRadius: 8,
                    backgroundColor: burned ? "#f3f3f3" : "white",
                    color: "#182a33",
                    boxSizing: "border-box",
                  }}
                />
              )}
            </div>
          );
        })}

        {error && (
          <p style={{ color: "#c25450", marginTop: 12, marginBottom: 0, fontSize: 14 }}>
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={burned}
          style={{
            width: "100%",
            marginTop: 20,
            padding: "12px 14px",
            fontSize: 16,
            fontWeight: 600,
            border: "none",
            borderRadius: 8,
            backgroundColor: burned ? "#bbb" : "#182a33",
            color: "white",
            cursor: burned ? "not-allowed" : "pointer",
          }}
        >
          Continue
        </button>
      </form>
    </div>
  );
}
